import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Code, Copy, Download, Eye, Database } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';

// ERP-specific API templates
const erpTemplates = {
  sap: {
    name: 'SAP ERP',
    logo: '🔷',
    request: {
      salesInvoice: `{
  "erpSystem": "SAP",
  "clientId": "CLT-001",
  "documentType": "INVOICE",
  "documentNumber": "INV-2024-0001",
  "documentDate": "2024-10-24",
  "postingDate": "2024-10-24",
  "dueDate": "2024-11-24",
  "companyCode": "1000",
  "salesOrganization": "SO01",
  "distributionChannel": "DC01",
  "customer": {
    "customerNumber": "C100001",
    "name": "Customer Company LLC",
    "taxNumber": "AE123456789",
    "street": "Sheikh Zayed Road",
    "city": "Dubai",
    "country": "AE",
    "postalCode": "00000"
  },
  "items": [
    {
      "itemNumber": "10",
      "materialNumber": "MAT-001",
      "description": "Product/Service Description",
      "quantity": 10,
      "unitOfMeasure": "EA",
      "unitPrice": 100.00,
      "netValue": 1000.00,
      "taxCode": "V5",
      "taxAmount": 50.00,
      "totalValue": 1050.00
    }
  ],
  "currency": "AED",
  "netAmount": 1000.00,
  "taxAmount": 50.00,
  "grossAmount": 1050.00
}`,
      purchaseInvoice: `{
  "erpSystem": "SAP",
  "clientId": "CLT-001",
  "documentType": "PURCHASE_INVOICE",
  "documentNumber": "PINV-2024-0001",
  "documentDate": "2024-10-24",
  "postingDate": "2024-10-24",
  "companyCode": "1000",
  "vendor": {
    "vendorNumber": "V200001",
    "name": "Supplier Company LLC",
    "taxNumber": "AE987654321",
    "street": "Industrial Area",
    "city": "Abu Dhabi",
    "country": "AE"
  },
  "purchaseOrder": "PO-2024-0150",
  "items": [
    {
      "itemNumber": "10",
      "materialNumber": "MAT-002",
      "description": "Raw Materials",
      "quantity": 20,
      "unitOfMeasure": "KG",
      "unitPrice": 50.00,
      "netValue": 1000.00,
      "taxCode": "V5",
      "taxAmount": 50.00
    }
  ],
  "currency": "AED",
  "netAmount": 1000.00,
  "taxAmount": 50.00,
  "grossAmount": 1050.00
}`
    }
  },
  oracle: {
    name: 'Oracle ERP Cloud',
    logo: '🔴',
    request: {
      salesInvoice: `{
  "erpSystem": "ORACLE",
  "clientId": "CLT-001",
  "InvoiceHeader": {
    "InvoiceNumber": "INV-2024-0001",
    "InvoiceDate": "2024-10-24",
    "InvoiceType": "Standard",
    "TransactionSource": "Manual",
    "LegalEntityId": "LE001",
    "BusinessUnit": "BU_DUBAI",
    "BillToCustomerId": 100001,
    "BillToCustomerName": "Customer Company LLC",
    "BillToCustomerNumber": "CUST-001",
    "BillToTaxRegistrationNumber": "AE123456789",
    "BillToAddress": {
      "AddressLine1": "Sheikh Zayed Road",
      "City": "Dubai",
      "Country": "AE",
      "PostalCode": "00000"
    },
    "Currency": "AED",
    "PaymentTerms": "NET30"
  },
  "InvoiceLines": [
    {
      "LineNumber": 1,
      "ItemNumber": "ITEM-001",
      "Description": "Product/Service",
      "Quantity": 10,
      "UnitOfMeasure": "EA",
      "UnitSellingPrice": 100.00,
      "LineAmount": 1000.00,
      "TaxClassificationCode": "VAT_5",
      "TaxAmount": 50.00
    }
  ],
  "InvoiceTotals": {
    "SubtotalAmount": 1000.00,
    "TotalTaxAmount": 50.00,
    "InvoiceAmount": 1050.00
  }
}`,
      purchaseInvoice: `{
  "erpSystem": "ORACLE",
  "clientId": "CLT-001",
  "InvoiceHeader": {
    "InvoiceNumber": "PINV-2024-0001",
    "InvoiceDate": "2024-10-24",
    "InvoiceType": "Standard",
    "Source": "SUPPLIER",
    "BusinessUnit": "BU_DUBAI",
    "SupplierId": 200001,
    "SupplierName": "Supplier Company LLC",
    "SupplierNumber": "SUPP-001",
    "SupplierTaxRegNumber": "AE987654321",
    "PurchaseOrder": "PO-2024-0150",
    "Currency": "AED"
  },
  "InvoiceLines": [
    {
      "LineNumber": 1,
      "ItemDescription": "Raw Materials",
      "Quantity": 20,
      "UnitOfMeasure": "KG",
      "UnitPrice": 50.00,
      "Amount": 1000.00,
      "TaxCode": "VAT_5",
      "TaxAmount": 50.00
    }
  ],
  "InvoiceTotals": {
    "LineAmount": 1000.00,
    "TaxAmount": 50.00,
    "TotalAmount": 1050.00
  }
}`
    }
  },
  tally: {
    name: 'Tally Prime',
    logo: '🟡',
    request: {
      salesInvoice: `{
  "erpSystem": "TALLY",
  "clientId": "CLT-001",
  "Voucher": {
    "VoucherType": "Sales",
    "VoucherNumber": "INV-2024-0001",
    "Date": "24-10-2024",
    "PartyLedgerName": "Customer Company LLC",
    "PartyGSTIN": "AE123456789",
    "PartyAddress": "Sheikh Zayed Road, Dubai, UAE",
    "PlaceOfSupply": "Dubai",
    "LedgerEntries": [
      {
        "LedgerName": "Sales Account",
        "Amount": -1000.00,
        "IsDebit": false
      },
      {
        "LedgerName": "VAT @ 5%",
        "Amount": -50.00,
        "IsDebit": false
      },
      {
        "LedgerName": "Customer Company LLC",
        "Amount": 1050.00,
        "IsDebit": true
      }
    ],
    "InventoryEntries": [
      {
        "ItemName": "Product/Service",
        "Quantity": 10,
        "Unit": "Nos",
        "Rate": 100.00,
        "Amount": 1000.00,
        "GodownName": "Main Warehouse"
      }
    ],
    "TotalAmount": 1050.00,
    "Currency": "AED"
  }
}`,
      purchaseInvoice: `{
  "erpSystem": "TALLY",
  "clientId": "CLT-001",
  "Voucher": {
    "VoucherType": "Purchase",
    "VoucherNumber": "PINV-2024-0001",
    "Date": "24-10-2024",
    "PartyLedgerName": "Supplier Company LLC",
    "PartyGSTIN": "AE987654321",
    "PartyAddress": "Industrial Area, Abu Dhabi, UAE",
    "PlaceOfSupply": "Abu Dhabi",
    "LedgerEntries": [
      {
        "LedgerName": "Purchase Account",
        "Amount": 1000.00,
        "IsDebit": true
      },
      {
        "LedgerName": "VAT @ 5%",
        "Amount": 50.00,
        "IsDebit": true
      },
      {
        "LedgerName": "Supplier Company LLC",
        "Amount": -1050.00,
        "IsDebit": false
      }
    ],
    "InventoryEntries": [
      {
        "ItemName": "Raw Materials",
        "Quantity": 20,
        "Unit": "Kg",
        "Rate": 50.00,
        "Amount": 1000.00,
        "GodownName": "Main Warehouse"
      }
    ],
    "TotalAmount": 1050.00,
    "Currency": "AED"
  }
}`
    }
  },
  zoho: {
    name: 'Zoho Books',
    logo: '🟣',
    request: {
      salesInvoice: `{
  "erpSystem": "ZOHO",
  "clientId": "CLT-001",
  "invoice_number": "INV-2024-0001",
  "date": "2024-10-24",
  "due_date": "2024-11-24",
  "customer_id": "100001",
  "customer_name": "Customer Company LLC",
  "gst_treatment": "registered_business",
  "gst_no": "AE123456789",
  "place_of_supply": "Dubai",
  "billing_address": {
    "attention": "Finance Department",
    "address": "Sheikh Zayed Road",
    "city": "Dubai",
    "state": "Dubai",
    "zip": "00000",
    "country": "UAE"
  },
  "line_items": [
    {
      "item_id": "ITEM-001",
      "name": "Product/Service",
      "description": "Product/Service Description",
      "quantity": 10,
      "unit": "pcs",
      "rate": 100.00,
      "amount": 1000.00,
      "tax_id": "VAT_5",
      "tax_percentage": 5
    }
  ],
  "currency_code": "AED",
  "sub_total": 1000.00,
  "tax_total": 50.00,
  "total": 1050.00,
  "payment_terms": 30,
  "payment_terms_label": "Net 30"
}`,
      purchaseInvoice: `{
  "erpSystem": "ZOHO",
  "clientId": "CLT-001",
  "bill_number": "PINV-2024-0001",
  "date": "2024-10-24",
  "due_date": "2024-11-24",
  "vendor_id": "200001",
  "vendor_name": "Supplier Company LLC",
  "gst_treatment": "registered_business",
  "gst_no": "AE987654321",
  "place_of_supply": "Abu Dhabi",
  "billing_address": {
    "address": "Industrial Area",
    "city": "Abu Dhabi",
    "state": "Abu Dhabi",
    "zip": "00000",
    "country": "UAE"
  },
  "line_items": [
    {
      "item_id": "ITEM-002",
      "name": "Raw Materials",
      "description": "Raw Materials Description",
      "quantity": 20,
      "unit": "kg",
      "rate": 50.00,
      "amount": 1000.00,
      "tax_id": "VAT_5",
      "tax_percentage": 5
    }
  ],
  "currency_code": "AED",
  "sub_total": 1000.00,
  "tax_total": 50.00,
  "total": 1050.00
}`
    }
  },
  dynamics: {
    name: 'Microsoft Dynamics 365',
    logo: '🔵',
    request: {
      salesInvoice: `{
  "erpSystem": "DYNAMICS",
  "clientId": "CLT-001",
  "@odata.type": "Microsoft.Dynamics.CRM.invoice",
  "name": "INV-2024-0001",
  "invoicedate": "2024-10-24",
  "duedate": "2024-11-24",
  "customerid@odata.bind": "/accounts(customer-guid-001)",
  "billto_line1": "Sheikh Zayed Road",
  "billto_city": "Dubai",
  "billto_country": "United Arab Emirates",
  "billto_postalcode": "00000",
  "transactioncurrencyid@odata.bind": "/transactioncurrencies(aed-guid)",
  "pricelevelid@odata.bind": "/pricelevels(price-level-guid)",
  "invoice_details": [
    {
      "@odata.type": "Microsoft.Dynamics.CRM.invoicedetail",
      "productid@odata.bind": "/products(product-guid-001)",
      "productdescription": "Product/Service",
      "quantity": 10,
      "uomid@odata.bind": "/uoms(uom-guid)",
      "priceperunit": 100.00,
      "baseamount": 1000.00,
      "tax": 50.00,
      "extendedamount": 1050.00
    }
  ],
  "totalamount": 1050.00,
  "totaltax": 50.00,
  "totalamountlessfreight": 1000.00
}`,
      purchaseInvoice: `{
  "erpSystem": "DYNAMICS",
  "clientId": "CLT-001",
  "@odata.type": "Microsoft.Dynamics.CRM.msdyn_purchaseinvoice",
  "msdyn_name": "PINV-2024-0001",
  "msdyn_invoicedate": "2024-10-24",
  "msdyn_vendorid@odata.bind": "/accounts(vendor-guid-001)",
  "msdyn_purchaseorder@odata.bind": "/msdyn_purchaseorders(po-guid)",
  "transactioncurrencyid@odata.bind": "/transactioncurrencies(aed-guid)",
  "msdyn_address1": "Industrial Area",
  "msdyn_city": "Abu Dhabi",
  "msdyn_country": "United Arab Emirates",
  "invoice_lines": [
    {
      "@odata.type": "Microsoft.Dynamics.CRM.msdyn_purchaseinvoiceline",
      "msdyn_product@odata.bind": "/products(product-guid-002)",
      "msdyn_description": "Raw Materials",
      "msdyn_quantity": 20,
      "msdyn_unitprice": 50.00,
      "msdyn_lineamount": 1000.00,
      "msdyn_tax": 50.00
    }
  ],
  "msdyn_subtotal": 1000.00,
  "msdyn_totaltax": 50.00,
  "msdyn_totalamount": 1050.00
}`
    }
  }
};

const responseTemplates = {
  success: `{
  "status": "success",
  "message": "Invoice processed successfully",
  "transactionId": "TXN-20241024-12345",
  "timestamp": "2024-10-24T10:30:00Z",
  "data": {
    "invoiceNumber": "INV-2024-0001",
    "aisReferenceNumber": "AIS-REF-12345",
    "ftaStatus": "forwarded",
    "ftaReferenceNumber": "FTA-2024-XYZ123",
    "serviceProviderStatus": "accepted"
  }
}`,
  error: `{
  "status": "error",
  "message": "Validation failed",
  "errorCode": "VAL_001",
  "timestamp": "2024-10-24T10:30:00Z",
  "errors": [
    {
      "field": "customer.taxNumber",
      "message": "Invalid tax number format",
      "code": "INVALID_FORMAT"
    },
    {
      "field": "items[0].quantity",
      "message": "Quantity must be greater than 0",
      "code": "INVALID_VALUE"
    }
  ]
}`
};

export default function APITemplates() {
  const [selectedErp, setSelectedErp] = useState<keyof typeof erpTemplates>('sap');

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const currentErp = erpTemplates[selectedErp];

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl text-gray-900">API Templates</h1>
        <p className="text-gray-600 mt-1">ERP-specific API integration templates and documentation</p>
      </div>

      {/* ERP Selector */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center space-x-4">
            <Database className="w-5 h-5 text-gray-600" />
            <div className="flex-1">
              <p className="text-sm text-gray-600 mb-2">Select ERP System</p>
              <Select value={selectedErp} onValueChange={(value) => setSelectedErp(value as keyof typeof erpTemplates)}>
                <SelectTrigger className="w-full md:w-96">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(erpTemplates).map(([key, erp]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center space-x-2">
                        <span>{erp.logo}</span>
                        <span>{erp.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Code className="w-6 h-6 text-[#0052CC]" />
              </div>
              <div>
                <p className="text-sm text-gray-600">API Version</p>
                <p className="text-lg text-gray-900">v2.0</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Eye className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Endpoint</p>
                <p className="text-sm text-gray-900">POST /api/v2/process</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Download className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Selected ERP</p>
                <p className="text-sm text-gray-900">{currentErp.logo} {currentErp.name}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* API Templates */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Request Templates for {currentErp.name}</CardTitle>
            <Badge variant="outline">{currentErp.logo}</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="sales" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="sales">Sales Invoice</TabsTrigger>
              <TabsTrigger value="purchase">Purchase Invoice</TabsTrigger>
            </TabsList>

            <TabsContent value="sales" className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-900">{currentErp.name} Sales Invoice Template</p>
                  <p className="text-sm text-gray-600">JSON structure specific to {currentErp.name}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(currentErp.request.salesInvoice)}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy
                </Button>
              </div>
              <pre className="bg-gray-900 text-green-400 p-6 rounded-lg text-sm overflow-x-auto max-h-[600px]">
{currentErp.request.salesInvoice}
              </pre>
            </TabsContent>

            <TabsContent value="purchase" className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-900">{currentErp.name} Purchase Invoice Template</p>
                  <p className="text-sm text-gray-600">JSON structure specific to {currentErp.name}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(currentErp.request.purchaseInvoice)}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy
                </Button>
              </div>
              <pre className="bg-gray-900 text-green-400 p-6 rounded-lg text-sm overflow-x-auto max-h-[600px]">
{currentErp.request.purchaseInvoice}
              </pre>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Response Templates - Same for all ERPs */}
      <Card>
        <CardHeader>
          <CardTitle>Response Templates (Standard for all ERPs)</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="success" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="success">Success Response</TabsTrigger>
              <TabsTrigger value="error">Error Response</TabsTrigger>
            </TabsList>

            <TabsContent value="success" className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Badge className="bg-green-500 mb-2">200 OK</Badge>
                  <p className="text-sm text-gray-600">Successful invoice processing response</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(responseTemplates.success)}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy
                </Button>
              </div>
              <pre className="bg-gray-900 text-green-400 p-6 rounded-lg text-sm overflow-x-auto">
{responseTemplates.success}
              </pre>
            </TabsContent>

            <TabsContent value="error" className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Badge className="bg-red-500 mb-2">400 Bad Request</Badge>
                  <p className="text-sm text-gray-600">Validation error response</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(responseTemplates.error)}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy
                </Button>
              </div>
              <pre className="bg-gray-900 text-red-400 p-6 rounded-lg text-sm overflow-x-auto">
{responseTemplates.error}
              </pre>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Integration Notes */}
      <Card>
        <CardHeader>
          <CardTitle>{currentErp.name} Integration Notes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {selectedErp === 'sap' && (
              <>
                <p className="text-sm text-gray-700">• SAP uses specific field names like <code className="bg-gray-100 px-2 py-1 rounded">companyCode</code>, <code className="bg-gray-100 px-2 py-1 rounded">salesOrganization</code></p>
                <p className="text-sm text-gray-700">• Material numbers and customer numbers follow SAP conventions</p>
                <p className="text-sm text-gray-700">• Tax codes use SAP tax classification (e.g., V5 for 5% VAT)</p>
              </>
            )}
            {selectedErp === 'oracle' && (
              <>
                <p className="text-sm text-gray-700">• Oracle uses OData naming conventions with PascalCase</p>
                <p className="text-sm text-gray-700">• Structured into Header and Lines sections</p>
                <p className="text-sm text-gray-700">• Requires LegalEntityId and BusinessUnit references</p>
              </>
            )}
            {selectedErp === 'tally' && (
              <>
                <p className="text-sm text-gray-700">• Tally uses voucher-based accounting structure</p>
                <p className="text-sm text-gray-700">• Date format is DD-MM-YYYY specific to Tally</p>
                <p className="text-sm text-gray-700">• Uses LedgerEntries for accounting and InventoryEntries for items</p>
              </>
            )}
            {selectedErp === 'zoho' && (
              <>
                <p className="text-sm text-gray-700">• Zoho uses snake_case naming convention</p>
                <p className="text-sm text-gray-700">• GST treatment fields are specific to tax compliance</p>
                <p className="text-sm text-gray-700">• Supports multiple tax jurisdictions and place of supply</p>
              </>
            )}
            {selectedErp === 'dynamics' && (
              <>
                <p className="text-sm text-gray-700">• Microsoft Dynamics uses OData protocol with @odata annotations</p>
                <p className="text-sm text-gray-700">• Entity references use @odata.bind with GUIDs</p>
                <p className="text-sm text-gray-700">• Field names are prefixed with entity type (e.g., msdyn_)</p>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
